"use client";

import { useEffect, useMemo, useState } from "react";

import Link from "next/link";

import { BookmarkPlus, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";

import ProductCard from "@/components/product/ProductCard";
import { SearchFacetBreadcrumbs } from "@/components/results/SearchFacetBreadcrumbs";
import { Button } from "@/components/ui/button";
import { useSavedSearches } from "@/contexts/SavedSearchesContext";
import { type SearchTool } from "@/lib/chat-message-parser";
import { type Product } from "@/lib/product-utils";
import { storedQueryFromMetadata } from "@/lib/saved-searches";
import { type SearchMetadata, searchMetadataStore } from "@/lib/search-metadata-store";
import { getShopperSearchPage, searchShopperProducts } from "@/lib/shop-agent-client";
import {
  type ResultsSearchState,
  resultsSearchState,
  shopperRequestFromIntent,
  shopperRequestFromQuery,
} from "@/lib/shopper-search";
import { productFromListItem } from "@/lib/shopper-search-items";
import { replayRequest, storedSearch } from "@/lib/stored-search";
import { lookupStoredSearch } from "@/lib/stored-search-lookup";

import type {
  ProgrammaticProductSearchEffectiveFacet,
  ShopperSearchPage,
  StoredSearch,
} from "openapi/shop_agent/client/types.gen";

const PREVIEW_RESULT_LIMIT = 4;

interface ChatSearchResultsPreviewProps {
  chatId: string;
  searchTool: SearchTool;
  isLoading: boolean;
  onViewFullResults: (searchId: string) => void;
}

export function ChatSearchResultsPreview({
  chatId,
  searchTool,
  isLoading,
  onViewFullResults,
}: ChatSearchResultsPreviewProps) {
  const searchId = searchTool.searchId;
  const [metadata, setMetadata] = useState<SearchMetadata | null>(() =>
    searchId ? searchMetadataStore.getSearchMetadata(searchId) : null
  );
  // A search recovered from history as a `/v1` request replays through the shopper route.
  const [recovered, setRecovered] = useState<StoredSearch | null>(null);
  const [ranFacets, setRanFacets] = useState<ProgrammaticProductSearchEffectiveFacet[] | null>(
    null
  );
  const [products, setProducts] = useState<Product[]>([]);
  const [isFetchingMetadata, setIsFetchingMetadata] = useState(false);
  const [isFetchingProducts, setIsFetchingProducts] = useState(false);
  const [isSavingSearch, setIsSavingSearch] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { savedSearches, createSavedSearch } = useSavedSearches();

  useEffect(() => {
    if (!searchId) {
      setMetadata(null);
      return;
    }

    const cachedMetadata = searchMetadataStore.getSearchMetadata(searchId);
    if (cachedMetadata) {
      setMetadata(cachedMetadata);
    }

    const unsubscribe = searchMetadataStore.subscribeToSearch(searchId, setMetadata);
    return unsubscribe;
  }, [searchId]);

  useEffect(() => {
    if (!searchId || metadata) return;

    let cancelled = false;
    setIsFetchingMetadata(true);

    lookupStoredSearch(searchId)
      .then((query) => {
        if (cancelled || !query) return;
        setRecovered(query);
        setMetadata(buildMetadataFromStoredSearch(searchId, query));
      })
      .catch((fetchError) => {
        console.debug("Unable to recover search metadata for chat preview:", fetchError);
      })
      .finally(() => {
        if (!cancelled) {
          setIsFetchingMetadata(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [metadata, searchId]);

  // The preview's search: a stored request, recovered here or cached by the
  // results page, replays as stored; a live search runs the agent's intent.
  // Null for one with neither (an image search).
  const search = useMemo<ResultsSearchState | null>(() => {
    if (!metadata) return null;
    const request = recovered
      ? replayRequest(recovered)
      : (metadata.request ?? shopperRequestFromIntent(metadata.intent));
    return request
      ? resultsSearchState(request, { browseMenuUuid: metadata.browseMenuUuid })
      : null;
  }, [metadata, recovered]);

  const savedSearch = useMemo(() => {
    if (!metadata?.searchId) return null;
    return savedSearches.find((search) => search.source_search_id === metadata.searchId) ?? null;
  }, [savedSearches, metadata?.searchId]);

  const handleSaveSearch = async () => {
    if (!metadata || isSavingSearch || savedSearch) return;

    setIsSavingSearch(true);
    try {
      // A search without a request (an image search) is saved as its history stores it.
      const searchQuery = search
        ? storedSearch(search.request, search.browseMenuUuid)
        : await lookupStoredSearch(metadata.searchId);
      if (!searchQuery) {
        toast.error("Could not save this search");
        return;
      }
      const created = await createSavedSearch({
        name: searchTool.label,
        searchQuery,
        sourceSearchId: metadata.searchId,
        threadId: chatId,
      });
      if (created) {
        toast.success("Search saved to collections");
      } else {
        toast.error("Sign in to save this search");
      }
    } catch (saveError) {
      console.error("Failed to save search:", saveError);
      toast.error("Could not save this search");
    } finally {
      setIsSavingSearch(false);
    }
  };

  // The preview runs its search at the preview's size; one without a request runs its query.
  const previewRequest = useMemo(() => {
    if (!metadata) return null;
    if (search) return { ...search.request, limit: PREVIEW_RESULT_LIMIT };
    return shopperRequestFromQuery(storedQueryFromMetadata(metadata), {
      limit: PREVIEW_RESULT_LIMIT,
    });
  }, [metadata, search]);

  useEffect(() => {
    if (!previewRequest || !searchId) return;

    let cancelled = false;
    setIsFetchingProducts(true);
    setError(null);

    // The chat agent's search cached its page 1 under the id; a miss runs the preview search.
    const loadPreviewPage = async (): Promise<ShopperSearchPage> => {
      try {
        const cached = await getShopperSearchPage(searchId);
        if (cached) return cached;
      } catch (cacheError) {
        console.debug("Cached search page unavailable for chat preview:", cacheError);
      }
      return searchShopperProducts(previewRequest);
    };

    loadPreviewPage()
      .then((page) => {
        if (cancelled) return;
        setRanFacets(page.effectiveQuery.facets ?? []);
        setProducts(page.items.map(productFromListItem).slice(0, PREVIEW_RESULT_LIMIT));
      })
      .catch((fetchError) => {
        console.error("Failed to load inline chat search results:", fetchError);
        if (!cancelled) {
          setProducts([]);
          setError("Could not load this product preview.");
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsFetchingProducts(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [previewRequest, searchId]);

  const breadcrumbFacets = ranFacets ?? metadata?.facets ?? [];
  const isWaiting = isLoading || isFetchingMetadata || (!metadata && !error);
  const isFetching = isWaiting || isFetchingProducts;
  const fullResultsHref = searchId ? `/results/${searchId}` : "#";

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-3 shadow-sm sm:p-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="text-xs font-medium uppercase tracking-[0.08em] text-ink-secondary">
            Search results
          </div>
          <h3 className="mt-1 truncate text-sm font-semibold text-ink sm:text-base">
            {searchTool.label}
          </h3>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 gap-1.5 rounded-full px-3 text-xs"
            onClick={handleSaveSearch}
            disabled={!metadata || isSavingSearch || Boolean(savedSearch)}
          >
            {isSavingSearch ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : savedSearch ? (
              <Check className="h-3.5 w-3.5" />
            ) : (
              <BookmarkPlus className="h-3.5 w-3.5" />
            )}
            {savedSearch ? "Saved" : "Save search"}
          </Button>
          {searchId && (
            <Link
              href={fullResultsHref}
              onClick={() => onViewFullResults(searchId)}
              className="text-xs font-medium text-ink underline underline-offset-4 hover:text-ink-secondary"
            >
              Open full results
            </Link>
          )}
        </div>
      </div>

      {breadcrumbFacets.length > 0 && (
        <div className="mt-3">
          <SearchFacetBreadcrumbs facets={breadcrumbFacets} />
        </div>
      )}

      {isFetching && (
        <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
          {Array.from({ length: PREVIEW_RESULT_LIMIT }).map((_, index) => (
            <div key={index} className="space-y-2">
              <div className="aspect-[3/4] animate-pulse rounded-xl bg-gray-100" />
              <div className="h-3 w-3/4 animate-pulse rounded bg-gray-100" />
              <div className="h-3 w-1/2 animate-pulse rounded bg-gray-100" />
            </div>
          ))}
        </div>
      )}

      {!isFetching && error && <p className="mt-3 text-sm text-ink-secondary">{error}</p>}

      {!isFetching && !error && products.length === 0 && (
        <p className="mt-3 text-sm text-ink-secondary">
          No products matched this preview. Open the full results to adjust filters.
        </p>
      )}

      {!isFetching && products.length > 0 && (
        <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
          {products.map((product, index) => (
            <ProductCard
              key={product.uuid ?? product.id ?? index}
              product={product}
              compact
              existingThreadId={chatId}
              priorityFirst={index === 0}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function buildMetadataFromStoredSearch(searchId: string, stored: StoredSearch): SearchMetadata {
  const { request } = stored;
  return {
    searchId,
    query: request.q ?? "",
    facets: (request.facets ?? []).map((facet) => ({
      name: facet.name,
      values: [...facet.values],
    })),
    priceMin: request.price_min ?? null,
    priceMax: request.price_max ?? null,
    similarToBrands: request.similar_to_brands ?? undefined,
    browseMenuUuid: stored.browse_menu_uuid ?? null,
    isComplete: true,
    timestamp: Date.now(),
  };
}
